import { motion } from 'motion/react'
import { type FC, type ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IcBaselineExpandMore from '~icons/ic/baseline-expand-more'
import LogosAmex from '~icons/logos/amex'
import LogosDiscover from '~icons/logos/discover'
import LogosJcb from '~icons/logos/jcb'
import LogosMastercard from '~icons/logos/mastercard'
import LogosUnionpay from '~icons/logos/unionpay'
import LogosVisa from '~icons/logos/visa'
import MaterialSymbolsLocalConvenienceStoreOutline from '~icons/material-symbols/local-convenience-store-outline'
import GooglePayMark from 'src/components/Payment/google-pay-mark.svg'
import {
  PAYMENT_PROVIDERS,
  type PaymentProviderConfig,
} from 'src/components/Payment/PaymentMethodSelectItem'
import { PaymentProvider } from 'src/services/helpers/payment'
import { cx } from 'src/utils/cx'

const MAX_VISIBLE = 5

interface ProviderTilesProps {
  selectedPaymentProvider: PaymentProvider | null
  eligibleProviders: { provider: PaymentProvider; preferred: boolean }[]
  onSelect: (provider: PaymentProvider) => void
}

export const ProviderTiles: FC<ProviderTilesProps> = ({
  selectedPaymentProvider,
  eligibleProviders,
  onSelect,
}) => {
  const { t } = useTranslation(['common'])
  const [expanded, setExpanded] = useState(false)

  const tiles = eligibleProviders
    .flatMap(({ provider, preferred }) => {
      const config = PAYMENT_PROVIDERS.find(p => p.id === provider)
      return config ? [{ config, preferred }] : []
    })
    // Push Conbini (GMO) to the end — per designer, it's the least prioritized
    // option even when otherwise eligible.
    .sort(
      (a, b) =>
        Number(a.config.id === PaymentProvider.gmo) -
        Number(b.config.id === PaymentProvider.gmo),
    )

  const canCollapse = tiles.length > MAX_VISIBLE
  // Reserve a row for the expander so the panel stays ≤ MAX_VISIBLE rows.
  const collapsedVisibleCount = MAX_VISIBLE - 1
  const alwaysVisibleTiles = canCollapse
    ? tiles.slice(0, collapsedVisibleCount)
    : tiles
  const collapsibleTiles = canCollapse ? tiles.slice(collapsedVisibleCount) : []

  return (
    <div className="flex flex-col gap-2">
      {alwaysVisibleTiles.map(({ config: tile, preferred }) => (
        <Tile
          key={tile.id}
          tile={tile}
          preferred={preferred}
          isSelected={tile.id === selectedPaymentProvider}
          onSelect={onSelect}
        />
      ))}
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          <div className="flex flex-col gap-2">
            {collapsibleTiles.map(({ config: tile, preferred }) => (
              <Tile
                key={tile.id}
                tile={tile}
                preferred={preferred}
                isSelected={tile.id === selectedPaymentProvider}
                onSelect={onSelect}
              />
            ))}
          </div>
        </motion.div>
      )}
      {canCollapse && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className={cx(
            'flex flex-row items-center gap-2 min-w-0',
            'px-3 py-2 rounded-xl',
            'ring-inset ring-1 ring-card-border',
            'text-primary/70 hover:text-primary',
            'hover:bg-hover transition-colors',
          )}
        >
          <span className="text-sm font-medium shrink-0 whitespace-nowrap">
            {t('common:action.more')} ({collapsibleTiles.length})
          </span>
          <IcBaselineExpandMore className="size-4 shrink-0" />
          <span className="ml-auto flex items-center gap-1.5 min-w-0 overflow-hidden">
            {collapsibleTiles.map(({ config: tile }) => {
              const isSelected = tile.id === selectedPaymentProvider
              return (
                <span
                  key={tile.id}
                  className={cx(
                    'flex items-center justify-center h-5 w-6 rounded shrink-0',
                    isSelected && 'bg-active',
                  )}
                >
                  <TileIcon tile={tile} />
                </span>
              )
            })}
          </span>
        </button>
      )}
    </div>
  )
}

interface TileProps {
  tile: PaymentProviderConfig
  preferred: boolean
  isSelected: boolean
  onSelect: (id: PaymentProvider) => void
}

const Tile: FC<TileProps> = ({ tile, preferred, isSelected, onSelect }) => {
  const { t } = useTranslation(['common', 'membership'])
  const badgeLabel = tile.new
    ? t('common:new.label', { defaultValue: 'New' })
    : preferred
      ? t('membership:payment.recommended', { defaultValue: 'Recommended' })
      : null
  return (
    <button
      onClick={() => onSelect(tile.id)}
      className={cx(
        'flex flex-row items-center gap-3 px-3 py-2 min-h-12 rounded-xl transition-all min-w-0',
        'text-primary text-left',
        isSelected
          ? 'ring-inset ring-2 ring-primary/60 bg-hover'
          : 'ring-inset ring-1 ring-card-border hover:bg-hover',
      )}
    >
      <span className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium leading-tight truncate">
          {tile.name}
        </span>
        {badgeLabel && (
          <span className="text-[10px] text-primary/60 leading-tight">
            {badgeLabel}
          </span>
        )}
      </span>
      <span className="flex items-center shrink-0">
        <TileIcon tile={tile} />
      </span>
    </button>
  )
}

const TileIcon: FC<{ tile: PaymentProviderConfig }> = ({ tile }) => {
  switch (tile.id) {
    case PaymentProvider.onerway:
      return (
        <CardBrandIconGroup
          brands={[
            <LogosVisa key="visa" />,
            <LogosMastercard key="mastercard" />,
            <LogosDiscover key="discover" />,
          ]}
        />
      )
    case PaymentProvider.onerwayJcbAeUnionPay:
      return (
        <CardBrandIconGroup
          brands={[
            <LogosJcb key="jcb" />,
            <LogosAmex key="amex" />,
            <LogosUnionpay key="unionpay" />,
          ]}
        />
      )
    case PaymentProvider.onerwayGooglePay:
      return <img src={GooglePayMark} alt="Google Pay" className="h-5 w-auto" />
    case PaymentProvider.gmo:
      return <MaterialSymbolsLocalConvenienceStoreOutline className="size-5" />
    default:
      return <>{tile.icon}</>
  }
}

const CardBrandIconGroup: FC<{ brands: ReactElement[] }> = ({ brands }) => (
  <div className="flex items-center gap-1">
    {brands.map((brand, index) => (
      <CardBrandIconTile key={brand.key ?? index}>{brand}</CardBrandIconTile>
    ))}
  </div>
)

const CardBrandIconTile: FC<{ children: ReactElement }> = ({ children }) => (
  <span className="flex h-5 w-8 items-center justify-center rounded-sm bg-white px-0.5 shadow-sm ring-1 ring-black/10 [&>svg]:max-h-3 [&>svg]:max-w-full">
    {children}
  </span>
)
